import isEqual from 'lodash/isEqual';
import { PAGES } from '../constants';
import { MAKE_RATE_CALL } from '../actions/quote';
import handleRateCallOne from '../actions/handleRateCallOne';
import handleRateCallTwo from '../actions/handleRateCallTwo';
import {
  ADD_ADDITIONAL_INSURED,
  REMOVE_ADDITIONAL_INSURED,
  SET_APPLICATION_TYPES,
  SET_BPP_DEDUCTIBLE,
  SET_EQUIPMENT_BREAKDOWN_COVERAGE,
  SET_GL_LIMIT,
  SET_HIRED_AND_NON_OWNED_AUTO,
  SET_LIQUOR_LIABILITY_COVERAGE,
  SET_RESTAURANT_COVERAGE,
  UPDATE_APPLICATION_FROM_SERVER,
  UPDATE_LOCATION,
  UPDATE_PROFESSIONAL_LIABILITY,
  SET_LOSS_CONTROL,
  SET_LIMIT_DAMAGE_PREM_RENTED,
  SET_CONTRACTOR_TOOLS_COVERAGE,
  SET_CYBER_COVERAGE,
  SET_WORKPLACE_VIOLENCE_COVERAGE,
  SET_EPLI_COVERAGE,
  SET_RETROACTIVE_DATE,
} from '../actions/application';
import isApplicationBindable from '../helpers/isApplicationBindable';
import addApplicationAggregate from '../helpers/addApplicationAggregate';
import { isAddOnsComplete } from '../selectors/progress';
import { selectQuotes } from '../selectors/quote';
import {
  selectPartnerAgencyExternalId,
  selectAgentProducerExternalId,
  selectAgentAgencyExternalId,
} from '../selectors';
import { AnyAction, Middleware } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { isApplicationQuotable } from '../helpers/isApplicationQuotable';
import { selectIsAgentMDA } from '../selectors/agent';
import { checkIfAddonsAreRateable } from '../helpers/checkIfAddonsAreComplete';
import { EndorsementKey } from '../Components/Endorsements/types';
import { endorsementCompleteSelectors } from '../Components/Endorsements/shared/checkIfEndorsementsAreComplete';
import { endorsementValidators } from '../Components/Endorsements/shared/endorsementValidation';
import { selectCheckedEndorsements, selectIsEndorsementsRefactorEnabled } from '../selectors/application';

const actionToEndorsementKey: Record<string, EndorsementKey> = {
  [SET_HIRED_AND_NON_OWNED_AUTO]: EndorsementKey.HiredAutoAndNonOwnedAuto,
  [SET_LIQUOR_LIABILITY_COVERAGE]: EndorsementKey.LiquorLiabilityEndorsement,
  [SET_RESTAURANT_COVERAGE]: EndorsementKey.RestaurantCoverage,
  [SET_EQUIPMENT_BREAKDOWN_COVERAGE]: EndorsementKey.EquipmentBreakdownEndorsement,
  [SET_LIMIT_DAMAGE_PREM_RENTED]: EndorsementKey.LimitDamagePremRented,
  [SET_CONTRACTOR_TOOLS_COVERAGE]: EndorsementKey.ContractorsInstallationAndTools,
  [SET_CYBER_COVERAGE]: EndorsementKey.CyberCoverage,
  [SET_WORKPLACE_VIOLENCE_COVERAGE]: EndorsementKey.WorkplaceViolenceCoverage,
  [SET_EPLI_COVERAGE]: EndorsementKey.EpliCoverage,
};

const rateCalls: Middleware<{}, ReduxState> = (store) => (next) => (action) => {
  const state = store.getState();
  const { application, location } = state;
  const { type } = action;
  const isSummaryPage = location.type === PAGES.SUMMARY;
  const isAddOnPage = location.type === PAGES.ADD_ONS;
  const hasQuotes = Boolean(selectQuotes(state).length);
  const isUserMDA = selectIsAgentMDA(store.getState());
  /**
   * Update the redux store and pull the updated application state by calling next(action)
   * so we can make the rate call with the updated application
   */
  const result = next(action);
  const { application: updated_application } = store.getState();

  const isEndorsementRateable = (endorsementKey: EndorsementKey): boolean => {
    const checkedEndorsements = selectCheckedEndorsements(store.getState());
    const isChecked = checkedEndorsements.includes(endorsementKey);

    // Unchecked means endorsement is being removed, always rerate
    if (!isChecked) return true;

    // verify endorsements selector has a limit actually selected
    const selector = endorsementCompleteSelectors[endorsementKey];
    if (selector && !Boolean(selector(store.getState()))) return false;

    // verify the limit is actually valid against the endorsements validator (if a validator exists)
    const validator = endorsementValidators[endorsementKey];
    if (validator && !validator(store.getState())) return false;

    return true;
  };

  const makeRateCall = ({ ignoreApplicationChanges } = { ignoreApplicationChanges: false }) => {
    const applicationHasUpdatedCheck = ignoreApplicationChanges || !isEqual(application, updated_application);

    const producerExternalId = selectAgentProducerExternalId(store.getState());
    const agencyExternalId =
      selectAgentAgencyExternalId(store.getState()) ?? selectPartnerAgencyExternalId(store.getState());
    const addProducerOrAgencyExternalId = isUserMDA ? { producerExternalId } : { agencyExternalId };

    if (isApplicationQuotable(updated_application) && applicationHasUpdatedCheck) {
      const application_with_agency_external_id = { ...updated_application, ...addProducerOrAgencyExternalId };
      const updated_application_with_aggregates = addApplicationAggregate(application_with_agency_external_id);

      const applicationPayload = updated_application_with_aggregates;

      (store.dispatch as ThunkDispatch<ReduxState, void, AnyAction>)(
        isApplicationBindable(applicationPayload)
          ? handleRateCallTwo(applicationPayload)
          : handleRateCallOne(applicationPayload)
      );
    }
  };
  switch (type) {
    case UPDATE_APPLICATION_FROM_SERVER:
    case REMOVE_ADDITIONAL_INSURED:
    case ADD_ADDITIONAL_INSURED:
    case SET_LOSS_CONTROL:
    case MAKE_RATE_CALL:
      makeRateCall();
      break;
    case SET_HIRED_AND_NON_OWNED_AUTO:
    case SET_LIQUOR_LIABILITY_COVERAGE:
    case SET_RESTAURANT_COVERAGE:
    case SET_EQUIPMENT_BREAKDOWN_COVERAGE:
    case SET_LIMIT_DAMAGE_PREM_RENTED:
    case SET_CONTRACTOR_TOOLS_COVERAGE:
    case SET_CYBER_COVERAGE:
    case SET_WORKPLACE_VIOLENCE_COVERAGE:
    case SET_EPLI_COVERAGE: {
      // only make a quote call on the summary or add-on page when endorsements are updated in the UI
      if (!(isSummaryPage || isAddOnPage)) break;

      if (selectIsEndorsementsRefactorEnabled(store.getState())) {
        const endorsementKey = actionToEndorsementKey[type];
        if (isEndorsementRateable(endorsementKey)) {
          makeRateCall();
        }
      } else {
        if (isAddOnsComplete(store.getState(), checkIfAddonsAreRateable)) {
          makeRateCall();
        }
      }
      break;
    }
    case PAGES.GENERAL_LIABILITY:
    case PAGES.PROFESSIONAL_LIABILITY:
    case SET_RETROACTIVE_DATE:
      makeRateCall({ ignoreApplicationChanges: true });
      break;
    case SET_BPP_DEDUCTIBLE:
    case UPDATE_LOCATION:
    case UPDATE_PROFESSIONAL_LIABILITY:
    case SET_GL_LIMIT:
      if (!isSummaryPage) {
        makeRateCall();
      }
      break;
    case PAGES.SUMMARY:
      if (!hasQuotes) {
        makeRateCall({ ignoreApplicationChanges: true });
      }
      break;
    case SET_APPLICATION_TYPES:
      if (isSummaryPage) {
        makeRateCall();
      }
      break;
    default:
      break;
  }

  return result;
};

export default rateCalls;
